'use client'

import React, { useCallback, useEffect, useState } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import { motion } from 'framer-motion'
import Image from 'next/image'
import { cn } from '@/utilities/ui'
import TeamMemberCard from '@/components/TeamMemberCard'
import { CarouselNavButton } from '@/components/ui/CarouselNavButton'
import { renderSimpleLexicalContent } from '@/utilities/renderSimpleLexical'
import type { QuoteWithTeamMember } from './Component'

interface TeamQuoteCarouselClientProps {
  quotes: QuoteWithTeamMember[]
  isDarkMode: boolean
}

export default function TeamQuoteCarouselClient({
  quotes,
  isDarkMode,
}: TeamQuoteCarouselClientProps) {
  const [emblaRef, embla] = useEmblaCarousel({ align: 'start', loop: false })
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [canLeft, setCanLeft] = useState(false)
  const [canRight, setCanRight] = useState(false)
  const [viewportHeight, setViewportHeight] = useState<number | null>(null)

  const onSelect = useCallback(() => {
    if (!embla) return
    setSelectedIndex(embla.selectedScrollSnap())
    setCanLeft(embla.canScrollPrev())
    setCanRight(embla.canScrollNext())
  }, [embla])

  useEffect(() => {
    if (!embla) return
    onSelect()
    embla.on('select', onSelect)
    embla.on('reInit', onSelect)
    return () => {
      embla.off('select', onSelect)
      embla.off('reInit', onSelect)
    }
  }, [embla, onSelect])

  const updateViewportHeight = useCallback(() => {
    if (!embla) return
    const currentIndex = embla.selectedScrollSnap()
    const slide = embla.slideNodes()[currentIndex]
    if (slide) {
      // Get the actual content height
      const contentHeight = slide.scrollHeight

      // Account for icon overflow space: -top-10 = 40px (2.5rem)
      // The pt-10 padding on the container creates visual space for the icon,
      // and we need to ensure the container height accommodates both content and icon
      const iconOverflowSpace = 40 // 2.5rem = -top-10 (matches pt-10 padding)
      const buffer = 20 // Safety buffer for rounding/measurement differences

      // Total height = content + icon overflow space + buffer
      // This ensures the container is always tall enough to prevent scrollbars
      setViewportHeight(contentHeight + iconOverflowSpace + buffer)
    }
  }, [embla])

  useEffect(() => {
    if (!embla) return
    updateViewportHeight()
    embla.on('select', updateViewportHeight)
    embla.on('reInit', updateViewportHeight)

    let observedSlide: Element | null = embla.slideNodes()[embla.selectedScrollSnap()] ?? null
    const resizeObserver = new ResizeObserver(() => updateViewportHeight())
    if (observedSlide) resizeObserver.observe(observedSlide as Element)

    const handleSlideChange = () => {
      const nextSlide = embla!.slideNodes()[embla!.selectedScrollSnap()] ?? null
      if (observedSlide && observedSlide !== nextSlide) {
        resizeObserver.unobserve(observedSlide)
      }
      observedSlide = nextSlide
      if (observedSlide) resizeObserver.observe(observedSlide as Element)
    }

    embla.on('select', handleSlideChange)
    embla.on('reInit', handleSlideChange)

    return () => {
      embla.off('select', updateViewportHeight)
      embla.off('reInit', updateViewportHeight)
      embla.off('select', handleSlideChange)
      embla.off('reInit', handleSlideChange)
      resizeObserver.disconnect()
    }
  }, [embla, updateViewportHeight])

  const scrollPrev = useCallback(() => embla && embla.scrollPrev(), [embla])
  const scrollNext = useCallback(() => embla && embla.scrollNext(), [embla])

  return (
    <div data-theme={isDarkMode ? 'dark' : undefined}>
      {/* Padding wrapper to accommodate icon overflow */}
      <div className="pt-15 overflow-visible">
        <div className="relative">
          <Image
            src="/icons/Quote Icon.svg"
            alt=""
            width={160}
            height={160}
            className="pointer-events-none select-none absolute -top-10 -left-10 lg:-left-15 w-16 h-16 md:w-24 md:h-24 lg:w-32 lg:h-32 z-0 opacity-10"
            aria-hidden
          />
          <div
            className="relative z-10 overflow-hidden transition-[height] duration-300 -mt-15 pt-10"
            ref={emblaRef}
            style={{ height: viewportHeight ? `${viewportHeight}px` : undefined }}
          >
            <div className="flex items-stretch">
              {quotes.map((item, index) => (
                <div key={item.id || index} className="min-w-0 shrink-0 grow-0 basis-full">
                  <div className="flex flex-col md:flex-row items-center md:items-stretch gap-8 md:gap-12 lg:gap-16">
                    <div className="shrink-0">
                      {selectedIndex === index ? (
                        <TeamMemberCard
                          key={`active-${item.id || index}`}
                          teamMember={item.teamMember}
                          index={0}
                          active
                        />
                      ) : (
                        <TeamMemberCard
                          key={`inactive-${item.id || index}`}
                          teamMember={item.teamMember}
                          index={0}
                          active={false}
                        />
                      )}
                    </div>
                    <motion.div
                      key={selectedIndex === index ? `quote-${index}` : `quote-hidden-${index}`}
                      initial={{ opacity: 0, y: 16 }}
                      animate={{
                        opacity: selectedIndex === index ? 1 : 0,
                        y: selectedIndex === index ? 0 : 16,
                      }}
                      transition={{
                        duration: 0.6,
                        ease: [0.25, 0.46, 0.45, 0.94],
                      }}
                      className={cn(
                        'flex-1 min-w-0 flex flex-col gap-10 justify-center',
                        isDarkMode ? 'on-dark' : 'text-text-primary',
                      )}
                    >
                      <div className="relative z-10 flex flex-col gap-4">
                        <h2 className="font-metropolis font-bold uppercase text-3xl md:text-3xl lg:text-5xl max-w-5xl">
                          {item.headline}
                        </h2>
                        <div
                          className={cn(
                            'text-base md:text-lg max-w-3xl richtext space-y-3',
                            isDarkMode ? 'on-dark-secondary' : 'text-text-secondary',
                          )}
                        >
                          {renderSimpleLexicalContent(item.quote)}
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <CarouselNavButton
                          direction="prev"
                          onClick={scrollPrev}
                          disabled={!canLeft}
                          aria-label="Zurück"
                        />
                        <CarouselNavButton
                          direction="next"
                          onClick={scrollNext}
                          disabled={!canRight}
                          aria-label="Weiter"
                        />
                      </div>
                    </motion.div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
